import { useTranslation } from "react-i18next";
import { useRef, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import {
  Plus,
  Mic,
  AudioLines,
  Send,
  Paperclip,
  Sparkles,
  Square,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn, getModelThumbnailUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useChatInput } from "../hooks/use-chat-actions";
import { useDictation } from "../hooks/use-dictation";
import { useLLMDialog, useLLMSelection } from "@/features/llm-selection/hooks";
import { useChatStore } from "@/features/chat";
import { insertTextAtCaret } from "../utils/insert-at-caret";
import { AttachmentsPreview } from "./AttachmentsPreview";
import { DictationRecorder } from "./DictationRecorder";
import {
  useSlashCommands,
  CommandSuggestionMenu,
  useCommandSuggestions,
  useSlashCommandFormSubmit,
} from "@/features/slash-commands";
import { ComposerSnake } from "./ComposerSnake";

interface MobileChatInputProps {
  // Optional custom handlers for different implementations (e.g., streaming)
  customMessage?: string;
  customSetMessage?: (message: string) => void;
  customHandleSubmit?: (e: React.FormEvent) => void;
  customIsLoading?: boolean;
  customIsSubmitting?: boolean;
  // Streaming devam ederken üretimi durdurmak için (stop generating)
  customHandleStop?: () => void;
  // Stop butonunun bağlama göre etiketi (ör. kuyruk varken "son mesajı kaldır")
  customStopLabel?: string;
}

export const MobileChatInput = ({
  customMessage,
  customSetMessage,
  customHandleSubmit,
  customIsLoading,
  customIsSubmitting,
  customHandleStop,
  customStopLabel,
}: MobileChatInputProps = {}) => {
  const { t } = useTranslation();

  // Reuse existing chat input logic
  const chatInput = useChatInput();

  // Use custom props if provided, otherwise use default chat input
  const message = customMessage ?? chatInput.message;
  const setMessage = customSetMessage ?? chatInput.setMessage;
  const isLoading = customIsLoading ?? chatInput.isLoading;
  const isSubmitting = customIsSubmitting ?? chatInput.isSubmitting;
  const handleSubmit = customHandleSubmit ?? chatInput.handleSubmit;

  // These are always from chatInput since they're not customizable
  const {
    removeAttachment,
    selectedAttachments,
    handleAIModelOpen,
    handleVoiceChatClick,
    // 🆕 File Picker Actions
    handleFilePickerOpen,
  } = chatInput;

  // LLM selection
  const { openModal: openLLMModal } = useLLMDialog();
  const { selectedModel, selectedModelName } = useLLMSelection();

  // Get thumbnail URL (R2 veya thumbnail_url)
  const selectedModelThumbnailUrl = selectedModel
    ? getModelThumbnailUrl(selectedModel)
    : null;

  const handleLLMModalOpen = () => {
    openLLMModal();
  };
  
  // 🎯 Slash Commands Integration
  const {
    isMenuOpen: isSlashMenuOpen,
    handleInputChange: handleSlashInputChange,
    handleCommandSelect: handleSlashCommandSelect,
    handleKeyDown: handleSlashKeyDown,
  } = useSlashCommands(setMessage);

  // Suggestions'ları al (keyboard navigation için)
  // NOT: Bu duplicate request'e sebep olmaz çünkü React hook'ları aynı input için cache'ler
  const { suggestions: slashSuggestions } = useCommandSuggestions(
    isSlashMenuOpen && message.startsWith("/") ? message : ""
  );

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const messageRef = useRef(message);
  messageRef.current = message;
  // Dikte açılmadan hemen önce caret konumu (ChatGPT gibi oraya yapıştırılır)
  const caretRef = useRef({ start: 0, end: 0 });
  const { focusRequestToken, setInputHeight } = useChatStore();
  
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [focusRequestToken]);

  // 🎙️ Dikte: transkript caret konumuna eklenir (sadece sona değil)
  const dictation = useDictation({
    onTranscribed: (text, { send }) => {
      const { next, caret } = insertTextAtCaret(
        messageRef.current,
        text,
        caretRef.current
      );
      // Gönder: state flush edilmeden submit eski metni okur.
      if (send && (next.trim() || selectedAttachments.length > 0)) {
        flushSync(() => setMessage(next));
        formRef.current?.requestSubmit();
        return;
      }
      setMessage(next);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(caret, caret);
      });
    },
  });
  const isDictating = dictation.status !== "idle";

  const captureCaret = () => {
    const el = textareaRef.current;
    if (!el) return;
    caretRef.current = {
      start: el.selectionStart,
      end: el.selectionEnd,
    };
  };

  const handleDictateStart = () => {
    // Blur sonrası da son bilinen caret kalsın; mümkünse canlı oku
    captureCaret();
    if (!textareaRef.current) {
      const len = messageRef.current.length;
      caretRef.current = { start: len, end: len };
    }
    void dictation.start();
  };

  // ChatGPT-style: Observe input container height and update store
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        // Add some padding for better spacing
        setInputHeight(height + 32); // 32px extra padding
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [setInputHeight]);

  const [hasExpanded, setHasExpanded] = useState(false);
  const lastLinesRef = useRef<number>(1);

  // Auto-grow up to 8 rows with smooth height transition & stable layout (no unnecessary recalcs)
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    const computed = window.getComputedStyle(el);
    const lineHeight = Math.max(1, parseFloat(computed.lineHeight || "20"));
    const paddingTop = parseFloat(computed.paddingTop || "0");
    const paddingBottom = parseFloat(computed.paddingBottom || "0");
    const maxRows = 8;

    const singleLineTotal = lineHeight + paddingTop + paddingBottom;
    const contentHeight = el.scrollHeight; // includes paddings, height reset gerekmez

    // Mesaj boşsa: inspect mod da dahil her zaman tek satıra sabitle
    if (message.trim().length === 0) {
      el.style.height = `${singleLineTotal}px`;
      if (hasExpanded) setHasExpanded(false);
      lastLinesRef.current = 1;
      return;
    }

    // Multiline tespiti ve kalıcı genişleme bayrağı
    const isMulti = contentHeight > singleLineTotal + 2;
    if (isMulti && !hasExpanded) setHasExpanded(true);

    // Satır sayısını hesapla ve yalnız değiştiğinde height güncelle
    const contentInner = contentHeight - paddingTop - paddingBottom;
    const lines = Math.max(1, Math.ceil(contentInner / lineHeight));
    const clampedLines = Math.min(lines, maxRows);
    const nextHeight = clampedLines * lineHeight + paddingTop + paddingBottom;
    if (clampedLines !== lastLinesRef.current) {
      el.style.height = `${nextHeight}px`;
      lastLinesRef.current = clampedLines;
    } else {
      const maxHeight = maxRows * lineHeight + paddingTop + paddingBottom;
      const current = parseFloat(el.style.height || "0");
      el.style.height = `${Math.min(current || nextHeight, maxHeight)}px`;
    }
  }, [message]);

  // Plus menüsü (dosya ekle / model seç / AI models / voice chat)
  // Collapsed ve expanded layout'ta aynı menü kullanılır.
  // modal={false}: menü item'ından Dialog (file picker vb.) açıldığında
  // body'de pointer-events:none takılı kalmasını önler.
  const renderComposerMenu = () => (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("landing.tools")}
          className="h-10 w-10 rounded-full bg-popover shadow-md ring-0"
        >
          <Plus className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="z-[1200] w-60 p-2 rounded-2xl border-none shadow-2xl bg-popover/95 bg-popover">
        <DropdownMenuItem onClick={handleFilePickerOpen}>
          <Paperclip className="h-4 w-4 mr-2" /> {t("landing.attachFile")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLLMModalOpen}>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {selectedModelThumbnailUrl ? (
              <img
                src={selectedModelThumbnailUrl}
                alt={selectedModel?.title}
                className="w-4 h-4 rounded object-contain flex-shrink-0"
              />
            ) : (
              <Sparkles className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="truncate">
              {selectedModel?.title || selectedModelName || t("chat.selectModel")}
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleAIModelOpen}>
          <Sparkles className="h-4 w-4 mr-2" /> {t("aiModels.title")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleVoiceChatClick}>
          <Mic className="h-4 w-4 mr-2" /> {t("chat.voiceChat")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const isBusy = isLoading || isSubmitting;
  // Kuyruk destekli composer (chat sayfası): streaming sürerken de yazma ve
  // gönderme açık kalır; gönderilen mesaj kuyruğa alınır, stop ayrıca durur.
  const canQueueWhileBusy = Boolean(customHandleSubmit && customHandleStop);
  const isInputLocked = isBusy && !canQueueWhileBusy;
  // Mesaj VEYA dosya varsa gönderilebilir (attachment-only gönderim desteklenir)
  const hasContent = message.trim().length > 0 || selectedAttachments.length > 0;
  const isDisabled = !hasContent || isInputLocked;

  // Streaming sırasında stop (+ içerik varsa send), normalde send/mic göster
  const renderPrimaryAction = () => {
    const showStop = isBusy && Boolean(customHandleStop);

    if (showStop && !canQueueWhileBusy) {
      return (
        <Button
          type="button"
          variant="darkPrimary"
          size="icon"
          className="h-10 w-10 rounded-full shadow-lg"
          onClick={customHandleStop}
          aria-label={t("chat.stopGenerating")}
          title={t("chat.stopGenerating")}
        >
          <Square className="h-4 w-4 fill-current" />
        </Button>
      );
    }

    return (
      <div className="flex items-center gap-1.5">
        {/* 🎙️ Dikte (speech-to-text) — her zaman görünür, metni input'a ekler */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-10 w-10 rounded-full shadow-lg"
            disabled={isInputLocked}
            onClick={handleDictateStart}
            aria-label={t("chat.dictate")}
            title={t("chat.dictate")}
          >
            <Mic className="h-5 w-5" />
          </Button>
        {/* ⏹️ Üretim sürerken stop, send'in yanında ayrıca durur.
            Kuyrukta mesaj varken önce en son ekleneni geri alır. */}
        {showStop && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-full shadow-lg"
            onClick={customHandleStop}
            aria-label={customStopLabel ?? t("chat.stopGenerating")}
            title={customStopLabel ?? t("chat.stopGenerating")}
          >
            <Square className="h-4 w-4 fill-current" />
          </Button>
        )}
        {hasContent ? (
          <Button
            type="submit"
            variant="darkPrimary"
            size="icon"
            className="h-10 w-10 rounded-full shadow-lg"
            disabled={isDisabled}
            aria-label={t("chat.sendMessage")}
          >
            <Send className="h-4 w-4" />
          </Button>
        ) : (
          !showStop && (
            /* 🔊 Sesli sohbet — ileride aktif edilecek */
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full shadow-lg bg-background border border-input"
              disabled
              onClick={handleVoiceChatClick}
              aria-label={t("chat.voiceChat")}
              title={t("chat.voiceChat")}
            >
              <AudioLines className="h-4 w-4" />
            </Button>
          )
        )}
      </div>
    );
  };

  
  // 🎯 Enhanced submit handler - slash command check (ortak hook)
  const handleFormSubmit = useSlashCommandFormSubmit(
    message,
    handleSubmit,
    handleSlashCommandSelect
  );

  
  return (
    <div
      ref={containerRef}
      className="max-w-3xl mx-auto absolute bottom-0 left-0 right-0 z-[60] px-4 pb-2 md:pb-6 flex flex-col items-center"
    >
      {/* 🆕 Universal Attachments Preview - All file types */}
      <AttachmentsPreview
        files={selectedAttachments}
        onRemove={removeAttachment}
        onAddMore={handleFilePickerOpen}
      />
      <form ref={formRef} onSubmit={handleFormSubmit} className="w-full">
        {/* Dış well. Yılan iç pill'in rim'inde (widget glass composer). */}
        <div
          data-slash-anchor
          className="bg-neutral-50 dark:bg-gray-800/50 p-2 rounded-[32px] w-full overflow-visible"
        >
          <div
            className="composer-snake-box"
            data-waiting={isBusy ? "true" : "false"}
          >
            <motion.div
            layout="position"
            className={cn(
              "relative flex flex-wrap items-end gap-2 p-2 rounded-3xl border",
              isBusy
                ? // Yılan dönerken pill opak: kaynakta da kutu sabit bir
                  // zemin, o yüzden halenin yalnızca dışa taşan kısmı
                  // görünüyor. Yarı saydam + backdrop-blur zeminde iç hale
                  // altından sızıyor ve bulanık bir yüzey gibi okunuyor.
                  "bg-white dark:bg-gray-800 border-border-tertiary"
                : "backdrop-blur-sm bg-white/95 dark:bg-gray-800/95 border-[#e3e8ef] dark:border-gray-700"
            )}
            style={{ willChange: "transform" }}
          >
            {isDictating ? (
              <div className="order-1 flex min-w-0 flex-1 items-center">
                <DictationRecorder
                  peaksRef={dictation.peaksRef}
                  durationMs={dictation.durationMs}
                  isTranscribing={dictation.isTranscribing}
                  canSend={!isInputLocked}
                  onCancel={dictation.cancel}
                  onConfirm={() => dictation.confirm()}
                  onConfirmAndSend={() => dictation.confirm({ send: true })}
                />
              </div>
            ) : (
              <>
            {/* Inline plus (only before expansion) */}
            {!hasExpanded && renderComposerMenu()}
            {/* Text composer - always order-1; full width after first expansion */}
            <motion.div
              layout="position"
              className={cn(
                hasExpanded
                  ? "order-1 basis-full min-w-0"
                  : "order-1 flex-1 min-w-0",
                "transition-all duration-200 ease-out flex relative"
              )}
            >
              {/* 🎯 Slash Command Menu - Portal ile body'e render */}
              {isSlashMenuOpen && (
                <CommandSuggestionMenu
                  inputValue={message}
                  isOpen={isSlashMenuOpen}
                  inputElement={textareaRef.current}
                  onSelect={handleSlashCommandSelect}
                />
              )}
              
              <textarea
                ref={textareaRef}
                rows={1}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  // 🎯 Handle slash commands
                  handleSlashInputChange(e.target.value);
                  // onChange sonrası caret güncelle (Enter ile alt satır vb.)
                  requestAnimationFrame(captureCaret);
                }}
                onSelect={captureCaret}
                onKeyUp={captureCaret}
                onClick={captureCaret}
                placeholder={t("chat.type")}
                disabled={isInputLocked}
                className={cn(
                  "w-full resize-none bg-transparent text-base px-3 py-2 rounded-xl placeholder:text-muted-foreground focus:outline-none ring-0 transition-[height] duration-200 ease-out",
                  hasExpanded ? "max-h-48 overflow-y-auto" : "overflow-hidden",
                  isInputLocked && "cursor-not-allowed"
                )}
                onKeyDown={(e) => {
                  // 🎯 Handle slash command keyboard navigation first
                  if (isSlashMenuOpen && message.startsWith('/')) {
                    // Arrow keys ve Enter/Tab için slash command handler'ı çağır
                    if (
                      e.key === "ArrowDown" ||
                      e.key === "ArrowUp" ||
                      e.key === "Enter" ||
                      e.key === "Tab" ||
                      e.key === "Escape"
                    ) {
                      handleSlashKeyDown(e, slashSuggestions);
                      
                      // Prevent normal behavior for these keys
                      if (
                        e.key === "Escape" ||
                        e.key === "ArrowUp" ||
                        e.key === "ArrowDown" ||
                        e.key === "Tab" ||
                        (e.key === "Enter" && !e.shiftKey)
                      ) {
                        e.preventDefault();
                        return;
                      }
                    }
                  }
                  
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (!isDisabled) {
                      const form = e.currentTarget.form;
                      if (form) form.requestSubmit();
                    }
                  }
                }}
                style={{
                  transition: "height 140ms ease",
                  willChange: "height",
                }}
              />
            </motion.div>

            {/* Inline right-side action (only before expansion) */}
            <AnimatePresence initial={false} mode="popLayout">
              {!hasExpanded && (
                <motion.div
                  layout="position"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.16, ease: "easeOut" }}
                  className="order-2 basis-auto flex items-center gap-2"
                >
                  {renderPrimaryAction()}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Bottom actions row - only after expansion */}
            <AnimatePresence initial={false} mode="popLayout">
              {hasExpanded && (
                <motion.div
                  layout="position"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className={cn(
                    "order-2 basis-full flex items-center justify-between gap-2"
                  )}
                >
                  {/* Plus popover - always bottom-left */}
                  {renderComposerMenu()}

                  {renderPrimaryAction()}
                </motion.div>
              )}
            </AnimatePresence>
              </>
            )}
            </motion.div>
            {isBusy ? <ComposerSnake radius={24} /> : null}
          </div>
        </div>

        {/* 🚫 Hidden File Input - REMOVED (file picker kullanılıyor) */}
      </form>
    </div>
  );
};

export default MobileChatInput;
